/**
 * Safe Markdown
 *
 * A narrow, dependency-free markdown-ish renderer for the client portal
 * (issue bodies + comments are now multi-author, including external client
 * content -- a real XSS surface, see project-brain/pages/client-outcomes.md
 * "Content safety"). Not a general markdown parser: it escapes all input
 * HTML first, then only ever builds output HTML from known-safe tags around
 * that already-escaped text, so there is no code path where raw user input
 * can become a tag or attribute. Handles the common subset GitHub issue
 * bodies actually use: headings, bold/italic, inline code, links (protocol-
 * allowlisted), lists, paragraphs.
 */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const ALLOWED_LINK_PROTOCOLS = ['http://', 'https://', 'mailto:'];

function isAllowedUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return ALLOWED_LINK_PROTOCOLS.some((p) => lower.startsWith(p));
}

/** Inline transforms applied to already-HTML-escaped text -- never raw input. */
function renderInline(escapedText: string): string {
  let html = escapedText;

  // Links: [text](url) -- url must pass the protocol allowlist (blocks
  // javascript:, data:, etc.); otherwise left as plain escaped text.
  html = html.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (match, text, url) => {
    if (!isAllowedUrl(url)) return match;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?:\*([^*\n]+)\*|_([^_\n]+)_)/g, (_m, a, b) => `<em>${a ?? b}</em>`);

  return html;
}

export function renderSafeMarkdown(rawText: string): string {
  const escaped = escapeHtml(rawText ?? '');
  const lines = escaped.split('\n');

  const blocks: string[] = [];
  let listBuffer: string[] = [];
  let paragraphBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push(`<ul>${listBuffer.map((item) => `<li>${renderInline(item)}</li>`).join('')}</ul>`);
      listBuffer = [];
    }
  };
  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      blocks.push(`<p>${paragraphBuffer.map(renderInline).join('<br>')}</p>`);
      paragraphBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      // Markdown h1 renders as h3 (capped at h6) so it never outranks the
      // page's own <h1>/<h2> hierarchy.
      const level = Math.min(headingMatch[1].length + 2, 6);
      blocks.push(`<h${level}>${renderInline(headingMatch[2])}</h${level}>`);
      continue;
    }

    const listMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      listBuffer.push(listMatch[1]);
      continue;
    }

    flushList();
    paragraphBuffer.push(trimmed);
  }

  flushParagraph();
  flushList();

  return blocks.join('');
}
